import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft, FiClock, FiDownload, FiUsers, FiGitBranch,
  FiWifi, FiWifiOff, FiFile, FiUpload, FiFileText,
  FiActivity, FiEdit3, FiMessageSquare, FiZap, FiSave, FiExternalLink,
  FiImage, FiLock, FiUser, FiChevronRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { formatRelativeTime } from '../../utils/dateFormat';
import { contractsAPI, documentsAPI, settingsAPI, downloadBlob } from '../../services/api';
import { hasPermission } from '../../utils/permissions';
import { withContractNumber } from '../../utils/documentNaming';
import { useWebSocket } from '../../hooks/useWebSocket';
import './DocumentEditor.scss';

const ONLYOFFICE_URL = process.env.REACT_APP_ONLYOFFICE_URL || 'http://localhost:8080';

// Deterministic color palette for user avatars
const USER_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4',
];

function getUserColor(userId) {
  if (!userId) return USER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

// Derive a real file type (PDF / Word / Excel / image / …) for a document so we
// can show a proper file icon — mirrors the Document Library behaviour.
function docFileType(doc) {
  if (doc?.isLocked || doc?.lockedPdfPath) return { key: 'pdf', ext: 'PDF' };
  const m = (doc?.title || '').match(/\.([a-z0-9]+)$/i);
  const e = m ? m[1].toLowerCase() : '';
  if (e === 'pdf') return { key: 'pdf', ext: 'PDF' };
  if (['doc', 'docx', 'rtf', 'odt'].includes(e)) return { key: 'word', ext: e.toUpperCase() };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(e)) return { key: 'excel', ext: e.toUpperCase() };
  if (['ppt', 'pptx'].includes(e)) return { key: 'ppt', ext: e.toUpperCase() };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(e)) return { key: 'image', ext: e.toUpperCase() };
  if (e) return { key: 'generic', ext: e.slice(0, 4).toUpperCase() };
  if (doc?.editorType === 'upload') return { key: 'generic', ext: 'FILE' };
  return { key: 'word', ext: 'DOCX' };
}

function DocFileGlyph({ doc }) {
  const { key, ext } = docFileType(doc);
  const Icon = key === 'image' ? FiImage : FiFileText;
  return (
    <span className={`de-file de-file--${key}`} aria-hidden="true">
      <Icon />
      <span className="de-file__tag">{ext}</span>
    </span>
  );
}

function getActivityIcon(activityType) {
  switch (activityType) {
    case 'typing':
    case 'editing':      return <FiEdit3 />;
    case 'suggestion':   return <FiZap />;
    case 'comment':      return <FiMessageSquare />;
    case 'version_saved': return <FiSave />;
    default:             return <FiActivity />;
  }
}

function DocumentEditor() {
  const { id: contractId, documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // "Back" returns to wherever the editor was opened from: the Document
  // Library (reopening the same folder), the contract detail (Contract
  // Documents tab), or — by default — the contract's documents page.
  const fromLibrary = location.state?.from === 'library';
  const fromContract = location.state?.from === 'contract';
  const backTo = fromLibrary ? '/documents'
    : fromContract ? `/contracts/${contractId}`
    : `/contracts/${contractId}/documents`;
  const backState = fromLibrary
    ? { reopen: { contractId, folderId: location.state?.folderId || null } }
    : fromContract ? { openTab: 'documents' }
    : undefined;
  const { user } = useSelector((state) => state.auth);
  const currentLanguage = useSelector((state) => state.language.currentLanguage);
  const { t } = useTranslation();
  // View and download are independent permissions — the version-history
  // download button only renders for users holding download_documents.
  const canDownload = hasPermission(user?.role, 'download_documents', user?.effectivePermissions);

  // Map app language codes to ONLYOFFICE BCP-47 locale codes
  const onlyofficeLocale = currentLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const editorContainerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorConfig, setEditorConfig] = useState(null);
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [showDocumentList, setShowDocumentList] = useState(!documentId);
  const [documents, setDocuments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draftDragOver, setDraftDragOver] = useState(false);
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [contractStatus, setContractStatus] = useState(null);
  // Suffixed onto every document name shown or downloaded here — see
  // utils/documentNaming.
  const [contractNumber, setContractNumber] = useState('');
  const [saving, setSaving] = useState(false);
  // Savers who clicked Save during this editor session — used for the
  // "N saved" counter next to the Save button. Keyed by userId.
  const [sessionSavers, setSessionSavers] = useState(new Map());
  // Track last shown activity toast to avoid duplicates
  const lastActivityToastRef = useRef(null);

  // Fetch company branding logo on mount
  useEffect(() => {
    settingsAPI.getBranding()
      .then(({ data }) => {
        if (data?.branding?.logoUrl) setBrandingLogoUrl(data.branding.logoUrl);
      })
      .catch(() => {}); // silently ignore — logo is cosmetic
  }, []);

  // Fetch contract status to determine if documents are locked
  useEffect(() => {
    if (contractId) {
      contractsAPI.get(contractId)
        .then(({ data }) => {
          setContractStatus(data.contract?.status || data.status);
          setContractNumber(data.contract?.contractNumber || '');
        })
        .catch(() => {});
    }
  }, [contractId]);

  const isContractLocked = ['approved', 'fully_executed', 'archived', 'terminated', 'sent_for_signature'].includes(contractStatus);

  // ─── Activity label helper (uses translations) ──────────────────────────────
  const getActivityLabel = useCallback((activityType, description) => {
    if (description) return description;
    switch (activityType) {
      case 'typing':        return t('documentEditor.activityTyping');
      case 'editing':       return t('documentEditor.activityEditing');
      case 'suggestion':    return t('documentEditor.activitySuggestion');
      case 'comment':       return t('documentEditor.activityComment');
      case 'version_saved': return t('documentEditor.activityVersionSaved');
      default:              return t('documentEditor.activityDefault');
    }
  }, [t]);

  // ─── WebSocket callbacks ────────────────────────────────────────────────────

  const handleVersionCreated = useCallback((data) => {
    // Track this saver for the session counter (de-dup by userId)
    if (data.savedByUserId) {
      setSessionSavers((prev) => {
        const next = new Map(prev);
        next.set(String(data.savedByUserId), data.savedBy || '');
        return next;
      });
    }

    // Only show the "new version" toast when it's actually a new version.
    // When multiple users save identical content, the backend appends to the
    // existing version's savedBy[] and emits with isNewVersion=false.
    if (data.isNewVersion === false) {
      toast.info(
        <span>
          <strong>{data.savedBy}</strong> {t('documentEditor.alsoSaved')}{' '}
          <strong>v{data.versionNumber}</strong>
        </span>,
        { autoClose: 4000 }
      );
    } else {
      toast.info(
        <span>
          <strong>{data.savedBy}</strong> {t('documentEditor.versionSaved')}{' '}
          <strong>v{data.versionNumber}</strong>
        </span>,
        { autoClose: 5000 }
      );
    }
    // Refresh versions list
    if (contractId && documentId) {
      documentsAPI.getVersions(contractId, documentId)
        .then(res => setVersions(res.data.versions || []))
        .catch(() => {});
    }
  }, [contractId, documentId, t]);

  const handleWorkflowInvalidated = useCallback((data) => {
    if (data.contractId === contractId) {
      toast.warning(t('documentEditor.workflowInvalidated'));
    }
  }, [contractId, t]);

  const handleUserActivity = useCallback((entry) => {
    // Debounce: don't show a toast for the same user within 8 seconds
    const key = entry.userId;
    const now = Date.now();
    if (
      lastActivityToastRef.current &&
      lastActivityToastRef.current.userId === key &&
      now - lastActivityToastRef.current.time < 8000
    ) {
      return;
    }
    lastActivityToastRef.current = { userId: key, time: now };

    const label = getActivityLabel(entry.activityType, entry.description);
    toast(
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 24, height: 24, borderRadius: '50%',
            background: getUserColor(entry.userId),
            color: '#fff', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 600, flexShrink: 0,
          }}
        >
          {entry.userName?.charAt(0)?.toUpperCase() || '?'}
        </span>
        <span>
          <strong>{entry.userName}</strong> {label}
        </span>
      </span>,
      { autoClose: 4000, icon: false }
    );
  }, [getActivityLabel]);

  // Get full user name from Redux state for presence display
  const fullUserName = user ? `${user.firstName} ${user.lastName}` : 'User';

  const { connected, activeUsers, activityFeed, typingUsers, broadcastActivity } = useWebSocket({
    documentId: documentId || null,
    userName: fullUserName,
    onVersionCreated: handleVersionCreated,
    onWorkflowInvalidated: handleWorkflowInvalidated,
    onUserActivity: handleUserActivity,
  });

  // ─── ONLYOFFICE integration ─────────────────────────────────────────────────

  // Broadcast activity when ONLYOFFICE fires events (injected via editorConfig events)
  const broadcastActivityRef = useRef(broadcastActivity);
  useEffect(() => { broadcastActivityRef.current = broadcastActivity; }, [broadcastActivity]);

  // Poll for version updates every 15 seconds as a fallback
  useEffect(() => {
    if (!contractId || !documentId) return;
    const interval = setInterval(() => {
      documentsAPI.getVersions(contractId, documentId)
        .then(res => setVersions(res.data.versions || []))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, [contractId, documentId]);

  // Load document list for the contract
  useEffect(() => {
    if (contractId && !documentId) {
      documentsAPI.list(contractId)
        .then(res => {
          setDocuments(res.data.documents || []);
          setShowDocumentList(true);
          setLoading(false);
        })
        .catch(() => {
          setError(t('documentEditor.loadFailed'));
          setLoading(false);
        });
    }
  }, [contractId, documentId, t]);

  // Load editor session when documentId is present
  useEffect(() => {
    if (!contractId || !documentId) return;

    setLoading(true);
    setShowDocumentList(false);

    Promise.all([
      documentsAPI.getEditorSession(contractId, documentId),
      documentsAPI.getVersions(contractId, documentId),
    ])
      .then(([sessionRes, versionsRes]) => {
        setEditorConfig(sessionRes.data.editorConfig);
        setDocument(sessionRes.data.document);
        setVersions(versionsRes.data.versions || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.error || t('documentEditor.sessionFailed'));
        setLoading(false);
      });
  }, [contractId, documentId, t]);

  // Initialize ONLYOFFICE editor when config is ready
  useEffect(() => {
    if (!editorConfig || !editorContainerRef.current) return;

    const scriptId = 'onlyoffice-api-script';
    let script = window.document.getElementById(scriptId);

    const initEditor = () => {
      if (window.DocsAPI) {
        try {
          // Inject collaboration event hooks and locale into the ONLYOFFICE config
          const enrichedConfig = {
            ...editorConfig,
            editorConfig: {
              ...(editorConfig.editorConfig || {}),
              lang: onlyofficeLocale,
            },
            events: {
              ...(editorConfig.events || {}),
              // Fired when the user makes any change in the document
              onDocumentStateChange: (event) => {
                // eslint-disable-next-line no-console
                console.log('[OO] onDocumentStateChange', event?.data, '(true=dirty, false=clean)');
                if (event?.data) {
                  // data=true means unsaved changes exist (user is editing)
                  broadcastActivityRef.current('editing');
                }
              },
              // Fired when a co-author makes a change (ONLYOFFICE built-in)
              onCollaborativeChanges: () => {
                // This fires on the receiving end — no need to broadcast
              },
              onRequestSaveAs: () => {
                // eslint-disable-next-line no-console
                console.log('[OO] onRequestSaveAs');
              },
              onError: (ev) => {
                // eslint-disable-next-line no-console
                console.error('[OO] editor error:', ev);
              },
            },
          };
          // eslint-disable-next-line no-new
          new window.DocsAPI.DocEditor('onlyoffice-editor', enrichedConfig);

          // Hide the ONLYOFFICE logo via CSS injected into the iframe after load
          const hideLogoInterval = setInterval(() => {
            const iframe = window.document.querySelector('#onlyoffice-editor iframe');
            if (iframe && iframe.contentDocument) {
              try {
                const style = iframe.contentDocument.createElement('style');
                style.textContent = `
                  .logo, .logo-wrapper, [class*="logo"], .asc-logo,
                  .header-logo, .toolbar-logo, .logo-img,
                  div[data-id="logo"], .logo-container,
                  .logo-left, .logo-right { display: none !important; }
                `;
                iframe.contentDocument.head.appendChild(style);
                clearInterval(hideLogoInterval);
              } catch (e) {
                // Cross-origin — can't inject CSS, clear interval
                clearInterval(hideLogoInterval);
              }
            }
          }, 500);
          // Stop trying after 10 seconds
          setTimeout(() => clearInterval(hideLogoInterval), 10000);
        } catch (err) {
          console.error('ONLYOFFICE initialization error:', err);
          setError(t('documentEditor.sessionFailed'));
        }
      }
    };

    if (!script) {
      script = window.document.createElement('script');
      script.id = scriptId;
      script.src = `${ONLYOFFICE_URL}/web-apps/apps/api/documents/api.js`;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => {
        setError(t('documentEditor.sessionFailed'));
      };
      window.document.head.appendChild(script);
    } else {
      initEditor();
    }

    return () => {
      const editorElement = window.document.getElementById('onlyoffice-editor');
      if (editorElement) editorElement.innerHTML = '';
    };
  }, [editorConfig, onlyofficeLocale, t]);

  // ─── Document creation helpers ──────────────────────────────────────────────

  // Same single-action draft upload as the contract's Belgeler tab: the file
  // is the whole interaction and its name becomes the document title.
  const handleCreateDocument = async (file) => {
    setCreating(true);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('title', file.name.replace(/\.[^/.]+$/, '').trim() || t('contractDetail.draftContract'));
        formData.append('file', file);
        res = await documentsAPI.create(contractId, formData);
      } else {
        res = await documentsAPI.create(contractId, { title: t('contractDetail.draftContract') });
      }
      const newDoc = res.data.document;
      toast.success(t('documentEditor.documentCreated', { name: newDoc.title }));
      if (file) {
        // Uploaded docs just appear in the grid — don't jump into the editor.
        setDocuments((prev) => [newDoc, ...prev.filter((d) => d._id !== newDoc._id)]);
        documentsAPI.list(contractId).then((r) => setDocuments(r.data.documents || [])).catch(() => {});
      } else {
        // A blank document is empty, so open the editor to start writing.
        navigate(`/contracts/${contractId}/documents/${newDoc._id}/editor`);
      }
    } catch (err) {
      toast.error(err.response?.data?.code === 'DRAFT_EXISTS'
        ? t('documentEditor.draftExists')
        : (err.response?.data?.error || t('documentEditor.createFailed')));
    } finally {
      setCreating(false);
    }
  };

  const handleDraftFilePicked = (file) => {
    if (!file || creating) return;
    if (!/\.(docx?|DOCX?)$/.test(file.name)) {
      toast.error(t('contractDetail.draftFileTypeInvalid'));
      return;
    }
    handleCreateDocument(file);
  };

  // Explicit save — asks the backend to trigger an OnlyOffice forcesave.
  // The version.created websocket event refreshes the versions list and
  // updates the session-savers counter; here we just show a confirmation toast.
  const handleSave = async () => {
    if (!contractId || !documentId || saving) return;
    setSaving(true);
    try {
      // Pass the live session's documentKey so the backend targets the same
      // OnlyOffice session even after currentVersion increments on prior saves.
      const documentKey = editorConfig?.document?.key;
      const res = await documentsAPI.save(contractId, documentId, documentKey);
      if (res.data?.noChanges) {
        toast.info(t('documentEditor.noChangesToSave'));
      } else {
        toast.success(t('documentEditor.saveChangesSuccess'));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('documentEditor.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Same rule as the contract page's download buttons: a locked document has
  // its cover+QR final PDF, and that is what every download must serve — the
  // working .docx only exists for documents still before the Lock/QR stage.
  const handleDownloadVersion = (versionNumber) => {
    const locked = document?.isLocked;
    const url = locked
      ? documentsAPI.downloadPdfUrl(contractId, documentId)
      : documentsAPI.downloadVersion(contractId, documentId, versionNumber);
    // L: shared axios instance — honours the impersonation/delegation token
    // store and the refresh interceptor, unlike a raw fetch + localStorage.
    downloadBlob(url)
      .then(res => {
        const blobUrl = window.URL.createObjectURL(res.data);
        const link = window.document.createElement('a');
        link.href = blobUrl;
        const base = `${document?.title || 'document'}${locked ? '_LockVersion' : `_v${versionNumber}`}`;
        link.setAttribute('download',
          `${withContractNumber(base, contractNumber)}${locked ? '.pdf' : '.docx'}`);
        window.document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(t('documentEditor.downloadFailed')));
  };

  // ─── Document List View ─────────────────────────────────────────────────────

  if (showDocumentList) {
    // One contract = one draft contract document: once any non-signed document
    // exists, adding another is disabled — changes go in as new versions.
    const hasDraftDoc = documents.some((d) => d.category !== 'signed' && d.category !== 'supporting');
    return (
      <div className="de-list">
        {/* Top bar */}
        <div className="de-list__topbar">
          <Link to={`/contracts/${contractId}`} state={{ openTab: 'documents' }} className="de-list__back">
            <FiArrowLeft /> {t('documentEditor.backToContract')}
          </Link>
          <FiChevronRight className="de-list__crumb-sep" />
          <span className="de-list__crumb"><FiFile /> {t('documentEditor.contractDocuments')}</span>
        </div>

        <div className="de-list__scroll">
          <div className="de-list__inner">
            {/* Header */}
            <header className="de-head">
              <div className="de-head__text">
                <span className="de-head__eyebrow">{t('documentEditor.contractDocuments')}</span>
                <h1 className="de-head__title">
                  {t('documentEditor.contractDocuments')}
                  {!loading && <span className="de-head__count">{documents.length}</span>}
                </h1>
                <p className="de-head__sub">
                  {t('documentEditor.listSubtitle', { defaultValue: 'Upload, edit and manage every document attached to this contract.' })}
                </p>
              </div>
            </header>

            {/* Locked notice */}
            {isContractLocked && (
              <div className="de-notice">
                <FiLock />
                <span>{t('documentEditor.lockedNotice', { defaultValue: 'This contract is locked — its documents can no longer be added or modified.' })}</span>
              </div>
            )}

            {/* Draft upload — one dropzone, no chooser and no confirm step, the
                same interaction as the contract's Belgeler tab. Unavailable
                once the single draft document exists. */}
            {!loading && !isContractLocked && !hasDraftDoc && (
              <div className="de-create">
                <label
                  className={`de-drop${draftDragOver ? ' de-drop--active' : ''}${creating ? ' de-drop--busy' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDraftDragOver(true); }}
                  onDragLeave={() => setDraftDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraftDragOver(false);
                    handleDraftFilePicked(e.dataTransfer.files?.[0]);
                  }}
                >
                  <span className="de-drop__icon"><FiUpload /></span>
                  <span className="de-drop__title">
                    {creating ? t('documentEditor.uploading') : t('contractDetail.draftDropTitle')}
                  </span>
                  <small>{t('contractDetail.draftDropHint')}</small>
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    accept=".doc,.docx"
                    disabled={creating}
                    onChange={(e) => {
                      const f = e.target.files[0];
                      e.target.value = '';
                      handleDraftFilePicked(f);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="de-create__blank"
                  disabled={creating}
                  onClick={() => handleCreateDocument(null)}
                >
                  <FiFileText /> {t('contractDetail.startBlankInstead')}
                </button>
              </div>
            )}

            {/* Documents — Windows-style icon grid */}
            {loading ? (
              <div className="de-files">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="de-tile de-tile--skeleton" aria-hidden="true">
                    <div className="de-shimmer de-shimmer--file" />
                    <div className="de-shimmer de-shimmer--line" />
                    <div className="de-shimmer de-shimmer--line de-shimmer--sm" />
                  </div>
                ))}
              </div>
            ) : documents.length === 0 ? (
              /* The dropzone above already says what to do — the empty state is
                 only for users who cannot add the draft. */
              isContractLocked ? (
                <div className="de-empty">
                  <div className="de-empty__icon"><FiFile /></div>
                  <h3>{t('documentEditor.noDocuments', { defaultValue: 'No documents yet' })}</h3>
                  <p>{t('documentEditor.noDocumentsDesc')}</p>
                </div>
              ) : null
            ) : (
              <div className="de-files">
                {documents.map((doc) => (
                  <Link
                    key={doc._id}
                    to={`/contracts/${contractId}/documents/${doc._id}/editor`}
                    className="de-tile"
                    title={withContractNumber(doc.title, contractNumber)}
                  >
                    <span className="de-tile__art">
                      <DocFileGlyph doc={doc} />
                      {doc.isLocked && <span className="de-tile__lock" aria-hidden="true"><FiLock /></span>}
                    </span>
                    <span className="de-tile__name">{withContractNumber(doc.title, contractNumber)}</span>
                    <span className="de-tile__meta">
                      v{doc.currentVersion}
                      {doc.createdAt && ` · ${formatRelativeTime(doc.createdAt, '')}`}
                    </span>
                    {doc.createdBy && (
                      <span className="de-tile__author"><FiUser /> {doc.createdBy.firstName} {doc.createdBy.lastName}</span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="document-editor">
        <div className="loading-spinner">{t('documentEditor.loadingEditor')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-editor">
        <div className="document-editor__header">
          <Link to={backTo} state={backState} className="document-editor__back">
            <FiArrowLeft /> {fromContract ? t('documentEditor.backToContract') : t('documentEditor.backToDocuments')}
          </Link>
        </div>
        <div className="document-editor__error">
          <h3>⚠️ {t('documentEditor.editorError')}</h3>
          <p>{error}</p>
          <p className="document-editor__hint">
            {t('documentEditor.editorErrorHint')}{' '}
            <code>docker run -i -t -d -p 8080:80 onlyoffice/documentserver</code>
          </p>
        </div>
      </div>
    );
  }

  // ─── Editor View ────────────────────────────────────────────────────────────

  const typingUsersList = Array.from(typingUsers.entries()); // [[userId, userName], ...]

  return (
    <div className="document-editor">
      {/* ── Top Bar ── */}
      <div className="document-editor__header">
        <div className="document-editor__header-left">
          {brandingLogoUrl && (
            <img
              src={brandingLogoUrl}
              alt="Logo"
              className="document-editor__brand-logo"
              draggable={false}
            />
          )}
          <Link to={backTo} state={backState} className="document-editor__back">
            <FiArrowLeft /> {fromContract ? t('documentEditor.backToContract') : t('documentEditor.documents')}
          </Link>
          <h2>{document?.title
            ? withContractNumber(document.title, contractNumber)
            : t('documentEditor.newDocument')}</h2>
          <span className="document-editor__version-badge">
            v{document?.currentVersion}
          </span>
        </div>

        <div className="document-editor__header-right">
          {/* Connection Status */}
          <span className={`document-editor__connection ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? <FiWifi /> : <FiWifiOff />}
            {connected ? t('documentEditor.live') : t('documentEditor.offline')}
          </span>

          {/* Presence Avatars */}
          {activeUsers.length > 0 && (
            <div className="document-editor__presence">
              <div className="document-editor__presence-avatars">
                {activeUsers.slice(0, 5).map((u, i) => (
                  <span
                    key={u.userId || i}
                    className="document-editor__presence-avatar"
                    style={{ background: getUserColor(u.userId), zIndex: 5 - i }}
                    title={u.name}
                  >
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                    {typingUsers.has(u.userId) && (
                      <span className="document-editor__typing-dot" />
                    )}
                  </span>
                ))}
                {activeUsers.length > 5 && (
                  <span className="document-editor__presence-avatar document-editor__presence-avatar--overflow">
                    +{activeUsers.length - 5}
                  </span>
                )}
              </div>
              <span className="document-editor__presence-label">
                <FiUsers />
                {activeUsers.length} {t('documentEditor.editing')}
              </span>
              <div className="document-editor__presence-tooltip">
                <strong>{t('documentEditor.editingNow')}</strong>
                {activeUsers.map((u, i) => (
                  <div key={i} className="document-editor__presence-user">
                    <span
                      className="document-editor__presence-avatar"
                      style={{ background: getUserColor(u.userId) }}
                    >
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                    <span className="document-editor__presence-name">{u.name}</span>
                    {typingUsers.has(u.userId) && (
                      <span className="document-editor__typing-badge">{t('documentEditor.isEditing')}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Button — only in editable contract states */}
          {!isContractLocked && (
            <button
              className="btn btn-primary btn-sm document-editor__save-btn"
              onClick={handleSave}
              disabled={saving}
              title={t('documentEditor.save')}
            >
              <FiSave />
              <span>{saving ? t('documentEditor.saving') : t('documentEditor.save')}</span>
              {sessionSavers.size > 0 && (
                <span className="document-editor__save-counter" title={Array.from(sessionSavers.values()).join(', ')}>
                  {sessionSavers.size} {sessionSavers.size === 1 ? t('documentEditor.userSaved') : t('documentEditor.usersSaved')}
                </span>
              )}
            </button>
          )}

          {/* Activity Feed Toggle */}
          <button
            className={`btn btn-outline btn-sm ${showActivity ? 'active' : ''}`}
            onClick={() => { setShowActivity(!showActivity); setShowVersions(false); }}
            title={t('documentEditor.liveActivity')}
          >
            <FiActivity />
            {activityFeed.length > 0 && (
              <span className="document-editor__badge">
                {Math.min(activityFeed.length, 9)}{activityFeed.length > 9 ? '+' : ''}
              </span>
            )}
          </button>

          {/* Version History Toggle */}
          <button
            className={`btn btn-outline btn-sm ${showVersions ? 'active' : ''}`}
            onClick={() => { setShowVersions(!showVersions); setShowActivity(false); }}
          >
            <FiClock /> {t('documentEditor.versions')}
          </button>
        </div>
      </div>

      {/* ── Typing Indicator Bar ── */}
      {typingUsersList.length > 0 && (
        <div className="document-editor__typing-bar">
          <div className="document-editor__typing-dots">
            <span /><span /><span />
          </div>
          <span className="document-editor__typing-text">
            {typingUsersList.length === 1
              ? <><strong>{typingUsersList[0][1]}</strong> {t('documentEditor.isEditing')}</>
              : <><strong>{typingUsersList.map(([, name]) => name).join(', ')}</strong> {t('documentEditor.areEditing')}</>
            }
          </span>
        </div>
      )}

      <div className="document-editor__body">
        {/* ── ONLYOFFICE Editor ── */}
        <div className="document-editor__editor-wrapper" ref={editorContainerRef}>
          <div id="onlyoffice-editor" className="document-editor__editor"></div>
        </div>

        {/* ── Live Activity Feed Sidebar ── */}
        {showActivity && (
          <div className="document-editor__sidebar document-editor__sidebar--activity">
            <div className="document-editor__sidebar-header">
              <h3><FiActivity /> {t('documentEditor.liveActivity')}</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setShowActivity(false)}>×</button>
            </div>

            {/* Active editors summary */}
            {activeUsers.length > 0 && (
              <div className="document-editor__active-editors">
                <p className="document-editor__active-editors-label">
                  <FiUsers />{' '}
                  {activeUsers.length}{' '}
                  {activeUsers.length === 1
                    ? t('documentEditor.personEditing')
                    : t('documentEditor.peopleEditing')}
                </p>
                <div className="document-editor__active-editors-list">
                  {activeUsers.map((u, i) => (
                    <div key={i} className="document-editor__active-editor-row">
                      <span
                        className="document-editor__presence-avatar document-editor__presence-avatar--sm"
                        style={{ background: getUserColor(u.userId) }}
                      >
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                      <span className="document-editor__active-editor-name">{u.name}</span>
                      {typingUsers.has(u.userId) && (
                        <span className="document-editor__typing-badge">{t('documentEditor.isEditing')}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="document-editor__activity-feed">
              {activityFeed.length === 0 ? (
                <div className="document-editor__activity-empty">
                  <FiActivity />
                  <p>{t('documentEditor.noActivity')}<br />{t('documentEditor.noActivityDesc')}</p>
                </div>
              ) : (
                activityFeed.map((entry) => (
                  <div
                    key={entry.id}
                    className={`document-editor__activity-item document-editor__activity-item--${entry.activityType}${entry.diffUrl ? ' document-editor__activity-item--clickable' : ''}`}
                    onClick={entry.diffUrl ? () => navigate(entry.diffUrl) : undefined}
                    role={entry.diffUrl ? 'button' : undefined}
                    tabIndex={entry.diffUrl ? 0 : undefined}
                    onKeyPress={entry.diffUrl ? (e) => e.key === 'Enter' && navigate(entry.diffUrl) : undefined}
                    title={entry.diffUrl ? t('documentEditor.viewChanges') : undefined}
                  >
                    <span
                      className="document-editor__activity-avatar"
                      style={{ background: getUserColor(entry.userId) }}
                    >
                      {entry.userName?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                    <div className="document-editor__activity-content">
                      <div className="document-editor__activity-header">
                        <span className="document-editor__activity-icon">
                          {getActivityIcon(entry.activityType)}
                        </span>
                        <strong className="document-editor__activity-user">{entry.userName}</strong>
                      </div>
                      <p className="document-editor__activity-desc">
                        {getActivityLabel(entry.activityType, entry.description)}
                      </p>
                      <div className="document-editor__activity-footer">
                        <span className="document-editor__activity-time">
                          {formatRelativeTime(entry.timestamp, '')}
                        </span>
                        {entry.diffUrl && (
                          <span className="document-editor__activity-view-link">
                            <FiExternalLink /> {t('documentEditor.viewChanges')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Version History Sidebar ── */}
        {showVersions && (
          <div className="document-editor__sidebar">
            <div className="document-editor__sidebar-header">
              <h3><FiClock /> {t('documentEditor.versionHistory')}</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setShowVersions(false)}>×</button>
            </div>
            <div className="document-editor__version-list">
              {versions.map((v) => {
                // Build the displayed saver list. Start from savedBy[] (populated by
                // the OnlyOffice save flow), fall back to createdBy for legacy
                // versions that predate the multi-user-save feature.
                const saversRaw = (v.savedBy && v.savedBy.length > 0)
                  ? v.savedBy.map(s => ({
                      userId: s.user?._id || s.user,
                      name: s.user ? `${s.user.firstName || ''} ${s.user.lastName || ''}`.trim() : s.userName,
                      savedAt: s.savedAt,
                    }))
                  : v.createdBy
                    ? [{
                        userId: v.createdBy._id,
                        name: `${v.createdBy.firstName || ''} ${v.createdBy.lastName || ''}`.trim(),
                        savedAt: v.createdAt,
                      }]
                    : [];
                // Dedupe by userId
                const seen = new Set();
                const savers = saversRaw.filter(s => {
                  const key = String(s.userId || s.name);
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                return (
                <div key={v._id} className="document-editor__version-item">
                  <div className="document-editor__version-info">
                    <strong>{t('documentEditor.version')} {v.versionNumber}</strong>
                    {savers.length > 0 && (
                      <div className="document-editor__version-savers">
                        <div className="document-editor__version-savers-avatars">
                          {savers.slice(0, 4).map((s, i) => (
                            <span
                              key={s.userId || i}
                              className="document-editor__version-saver-avatar"
                              style={{ background: getUserColor(String(s.userId || s.name)), zIndex: 4 - i }}
                              title={s.name}
                            >
                              {s.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          ))}
                          {savers.length > 4 && (
                            <span className="document-editor__version-saver-avatar document-editor__version-saver-avatar--overflow">
                              +{savers.length - 4}
                            </span>
                          )}
                        </div>
                        <span className="document-editor__version-meta">
                          {savers.length === 1
                            ? savers[0].name
                            : t('documentEditor.savedByCount', { count: savers.length })}
                        </span>
                      </div>
                    )}
                    <span className="document-editor__version-time">
                      {v.createdAt && formatRelativeTime(v.createdAt, '')}
                    </span>
                    {v.changeSummary && (
                      <span className="document-editor__version-summary">{v.changeSummary}</span>
                    )}
                  </div>
                  <div className="document-editor__version-actions">
                    {canDownload && (
                      <button
                        className="btn btn-xs btn-outline"
                        onClick={() => handleDownloadVersion(v.versionNumber)}
                        title={t('documentEditor.download')}
                      >
                        <FiDownload />
                      </button>
                    )}
                    {v.versionNumber > 1 && (
                      <Link
                        to={`/contracts/${contractId}/documents/${documentId}/compare?v1=${v.versionNumber - 1}&v2=${v.versionNumber}`}
                        className="btn btn-xs btn-outline"
                        title={t('documentEditor.compareVersions')}
                      >
                        <FiGitBranch />
                      </Link>
                    )}
                  </div>
                </div>
                );
              })}
              {versions.length === 0 && (
                <p className="document-editor__empty-text">{t('documentEditor.noVersions')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentEditor;
